import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Trophy, Star, ArrowLeft, Target, Play, Loader2, AlertCircle } from 'lucide-react';
import { useQuizAttemptById } from '@/hooks/useQuizAttemptById';
import { usePlayerQuizzes, useQuizAttempt } from '@/hooks/usePlayerQuizzes';

const QuizResultsPage = () => {
  const { playerId, programId, quizId, attemptId } = useParams<{
    playerId: string;
    programId: string;
    quizId: string;
    attemptId: string;
  }>();
  const navigate = useNavigate();

  const { attempt, quiz, loading, error } = useQuizAttemptById(attemptId);
  const { quizzes } = usePlayerQuizzes(programId, playerId);
  const { startQuizAttempt } = useQuizAttempt(playerId);

  const handleBack = () => {
    navigate(`/player/${playerId}/programs/${programId}`);
  };

  const handleTakeAgain = async () => {
    if (!quiz || !playerId) return;
    
    const newAttempt = await startQuizAttempt(quiz.id);
    if (newAttempt) {
      // Navigate back to program page where quiz interface will open
      navigate(`/player/${playerId}/programs/${programId}`);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 80) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 70) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getPerformanceMessage = (score: number) => {
    if (score >= 90) return { title: 'Excellent!', message: 'Outstanding performance!' };
    if (score >= 80) return { title: 'Great Job!', message: 'Well done on this quiz!' };
    if (score >= 70) return { title: 'Good Work!', message: 'Nice effort, keep it up!' };
    return { title: 'Keep Trying!', message: 'Practice makes perfect!' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-lg text-muted-foreground">Loading results...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !attempt || !quiz) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-md mx-auto">
            <CardContent className="text-center py-16">
              <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Results Not Found</h2>
              <p className="text-muted-foreground mb-4">{error || 'The quiz results could not be found.'}</p>
              <Button onClick={handleBack} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const performance = getPerformanceMessage(attempt.score || 0);
  const canRetake = quiz.attempts_allowed > (attempt.attempt_number || 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-4 sm:py-8 max-w-4xl">
        {/* Breadcrumb Navigation */}
        <div className="mb-4 sm:mb-6">
          <Breadcrumb>
            <BreadcrumbList className="flex-wrap">
              <BreadcrumbItem>
                <BreadcrumbLink onClick={() => navigate(`/player/${playerId}/programs`)}>
                  Programs
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink onClick={handleBack}>
                  Program
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Quiz Results</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Header */}
        <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleBack}
            className="flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground truncate">
              Quiz Results
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground truncate">
              {quiz.title}
            </p>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {/* Main Score Display */}
          <Card className="border-0 bg-gradient-to-br from-blue-50 to-purple-50">
            <CardContent className="text-center p-4 sm:p-6 lg:p-8">
              <div className="mb-4 sm:mb-6">
                <Trophy className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-yellow-500 mb-2" />
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground">{performance.title}</h2>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">{performance.message}</p>
              </div>
              
              <div className={`inline-flex items-center justify-center w-24 h-24 sm:w-32 sm:h-32 rounded-full text-2xl sm:text-4xl font-bold border-2 ${getScoreColor(attempt.score || 0)}`}>
                {attempt.score || 0}%
              </div>
            </CardContent>
          </Card>

          {/* Detailed Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <Card>
              <CardContent className="text-center p-3 sm:p-4">
                <Star className="h-6 w-6 sm:h-8 sm:w-8 mx-auto text-yellow-500 mb-2" />
                <div className="text-xl sm:text-2xl font-bold text-foreground">{attempt.total_points || 0}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">Points Earned</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="text-center p-3 sm:p-4">
                <Target className="h-6 w-6 sm:h-8 sm:w-8 mx-auto text-blue-500 mb-2" />
                <div className="text-xl sm:text-2xl font-bold text-foreground">
                  {attempt.total_points || 0}/{attempt.max_possible_points || 0}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">Score</div>
              </CardContent>
            </Card>

            <Card className="sm:col-span-2 lg:col-span-1">
              <CardContent className="text-center p-3 sm:p-4">
                <Play className="h-6 w-6 sm:h-8 sm:w-8 mx-auto text-green-500 mb-2" />
                <div className="text-xl sm:text-2xl font-bold text-foreground">
                  #{attempt.attempt_number || 1}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">Attempt</div>
              </CardContent>
            </Card>
          </div>

          {/* Quiz Info */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <span className="text-sm sm:text-base text-muted-foreground">Quiz:</span>
                <span className="font-medium text-sm sm:text-base break-words">{quiz.title}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <span className="text-sm sm:text-base text-muted-foreground">Attempt:</span>
                <Badge variant="outline">#{attempt.attempt_number || 1} of {quiz.attempts_allowed}</Badge>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <span className="text-sm sm:text-base text-muted-foreground">Completed:</span>
                <span className="text-xs sm:text-sm">
                  {attempt.completed_at ? new Date(attempt.completed_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Points Notification */}
          {(attempt.total_points || 0) > 0 && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <Trophy className="h-5 w-5 sm:h-6 sm:w-6 inline-block text-green-600 mr-2" />
                <span className="text-green-800 font-medium text-sm sm:text-base">
                  You earned {attempt.total_points || 0} points! 🎉
                </span>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 pt-4">
            {canRetake && (
              <Button 
                onClick={handleTakeAgain} 
                className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-primary-foreground"
              >
                <Play className="w-4 h-4 mr-2" />
                Take Again
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizResultsPage;